// ============== SEO PAGES DATA & COMPONENTS ==============
// Service + Industry Solutions and Location Pages for SEO

import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  ArrowRight, Check, Users, DollarSign, Settings, Headphones,
  Search, TrendingUp, CalendarCheck, Database, LineChart, ClipboardCheck,
  FolderKanban, Workflow, Building2, MapPin, Globe, Zap, Shield, Target,
  ChevronRight
} from 'lucide-react';
import { solutionContent, getSolutionImages, getLocationContent, getLocationImages } from './seoContent';
import InternalLinks from './InternalLinks';

// ============== SOLUTION PAGES DATA ==============

// Industries for solutions
const solutionIndustries = {
  technology: {
    id: 'technology',
    name: 'Technology Companies',
    slug: 'technology-companies',
    shortName: 'Technology',
    theme: {
      focus: ['speed', 'scalability', 'lean growth', 'operational efficiency', 'support for growing teams'],
      challenges: [
        'Rapid team scaling requirements',
        'Need for cost-efficient growth',
        'Limited bandwidth for administrative functions',
        'Pressure to maintain lean operations',
        'Competition for internal resources'
      ]
    }
  },
  staffing: {
    id: 'staffing',
    name: 'Staffing Companies',
    slug: 'staffing-companies',
    shortName: 'Staffing',
    theme: {
      focus: ['candidate volumes', 'process speed', 'recruiter productivity', 'admin reduction', 'margin protection'],
      challenges: [
        'High candidate volumes requiring rapid processing',
        'Time-consuming screening and scheduling tasks',
        'Administrative burden on revenue-generating staff',
        'Margin pressure from internal overhead',
        'Need for faster placement cycles'
      ]
    }
  },
  'financial-services': {
    id: 'financial-services',
    name: 'Financial Services Firms',
    slug: 'financial-services-firms',
    shortName: 'Financial Services',
    theme: {
      focus: ['accuracy', 'reporting discipline', 'process control', 'documentation', 'operational consistency'],
      challenges: [
        'Documentation-heavy workflows',
        'High accuracy and consistency requirements',
        'Complex reporting and compliance pressure',
        'Need for rigorous process control',
        'Cost pressure on operational functions'
      ]
    }
  }
};

// Services for solutions (primary 8)
const solutionServices = {
  'hr-operations': {
    id: 'hr-operations',
    name: 'HR Operations',
    slug: 'hr-operations',
    icon: Users,
    servicePath: '/services/hr',
    capabilities: {
      technology: ['Recruitment coordination for tech roles', 'Onboarding for distributed teams', 'HR reporting and analytics', 'Payroll administration', 'Employee lifecycle support', 'Compliance coordination'],
      staffing: ['High-volume candidate processing', 'Recruiter administrative support', 'Payroll coordination', 'Contractor onboarding', 'Compliance documentation', 'HR reporting'],
      'financial-services': ['Regulatory-compliant HR processes', 'Documentation management', 'Payroll administration', 'Employee records maintenance', 'Compliance reporting', 'Onboarding coordination']
    },
    benefits: ['Lower HR operating costs', 'Faster hiring cycles', 'Improved compliance', 'Scalable HR capacity']
  },
  'finance-accounting': {
    id: 'finance-accounting',
    name: 'Finance & Accounting',
    slug: 'finance-accounting',
    icon: DollarSign,
    servicePath: '/services/finance',
    capabilities: {
      technology: ['Accounts payable/receivable', 'Monthly reconciliations', 'Financial reporting', 'Expense management', 'Revenue recognition support', 'Bookkeeping'],
      staffing: ['Contractor payment processing', 'Client invoicing support', 'Reconciliation of placements', 'Commission calculations', 'Financial reporting', 'Margin analysis support'],
      'financial-services': ['High-accuracy transaction processing', 'Regulatory reporting support', 'Reconciliation and verification', 'Documentation and audit trails', 'Financial data management', 'Compliance reporting']
    },
    benefits: ['Reduced finance costs', 'Improved reporting accuracy', 'Faster close cycles', 'Better financial visibility']
  },
  'business-operations': {
    id: 'business-operations',
    name: 'Business Operations',
    slug: 'business-operations',
    icon: Settings,
    servicePath: '/services/operations',
    capabilities: {
      technology: ['Back-office process support', 'CRM and system updates', 'Workflow coordination', 'Data processing', 'Administrative functions', 'Reporting support'],
      staffing: ['Placement coordination', 'Contractor management support', 'Client communication', 'Database maintenance', 'Process administration', 'Operational reporting'],
      'financial-services': ['Document processing', 'Data verification', 'Workflow administration', 'Client service support', 'Records management', 'Operational reporting']
    },
    benefits: ['Streamlined operations', 'Reduced administrative burden', 'Improved process consistency', 'Scalable support model']
  },
  'customer-support': {
    id: 'customer-support',
    name: 'Customer Support',
    slug: 'customer-support',
    icon: Headphones,
    servicePath: '/services/customer-support',
    capabilities: {
      technology: ['Technical support triage', 'Customer inquiry handling', 'Ticket management', 'Knowledge base support', 'Email and chat support', 'Customer feedback processing'],
      staffing: ['Candidate inquiry handling', 'Client communication support', 'Placement follow-up', 'Issue resolution', 'Survey and feedback management', 'Multi-channel support'],
      'financial-services': ['Client inquiry handling', 'Account support', 'Documentation requests', 'Service coordination', 'Complaint management', 'Quality assurance']
    },
    benefits: ['Extended support coverage', 'Improved response times', 'Better customer satisfaction', 'Scalable support capacity']
  },
  'recruitment-support': {
    id: 'recruitment-support',
    name: 'Recruitment Support',
    slug: 'recruitment-support',
    icon: Search,
    servicePath: '/services/recruitment',
    capabilities: {
      technology: ['Tech talent sourcing', 'Resume screening', 'Interview scheduling', 'ATS management', 'Candidate communication', 'Pipeline administration'],
      staffing: ['High-volume sourcing', 'Rapid resume screening', 'Interview coordination', 'Candidate tracking', 'Database management', 'Recruiter productivity support'],
      'financial-services': ['Specialized talent sourcing', 'Credential verification', 'Interview scheduling', 'Background check coordination', 'Candidate communication', 'Compliance documentation']
    },
    benefits: ['Faster hiring processes', 'Lower recruiting costs', 'Improved candidate experience', 'Greater recruiter productivity']
  },
  'data-services': {
    id: 'data-services',
    name: 'Data Services',
    slug: 'data-services',
    icon: Database,
    servicePath: '/services/data-services',
    capabilities: {
      technology: ['Data entry and validation', 'CRM updates', 'Data cleansing', 'Reporting support', 'System migration support', 'Database maintenance'],
      staffing: ['Candidate data management', 'ATS data hygiene', 'Client database updates', 'Placement tracking', 'Reporting support', 'Data enrichment'],
      'financial-services': ['High-accuracy data entry', 'Document digitization', 'Data verification', 'Regulatory data management', 'Audit trail maintenance', 'Reporting data preparation']
    },
    benefits: ['Improved data quality', 'Reduced manual workload', 'Better reporting reliability', 'Faster data processing']
  },
  'executive-assistant': {
    id: 'executive-assistant',
    name: 'Executive Assistant Support',
    slug: 'executive-assistant',
    icon: CalendarCheck,
    servicePath: '/services/executive-assistants',
    capabilities: {
      technology: ['Calendar management', 'Meeting coordination', 'Travel arrangements', 'Document preparation', 'Research support', 'Communication management'],
      staffing: ['Executive scheduling', 'Client meeting coordination', 'Travel logistics', 'Presentation support', 'Administrative coordination', 'Follow-up tracking'],
      'financial-services': ['Compliance-aware scheduling', 'Client meeting preparation', 'Document management', 'Travel coordination', 'Meeting minutes', 'Administrative support']
    },
    benefits: ['Increased executive productivity', 'Better organization', 'Reduced administrative burden', 'More time for strategic work']
  },
  'sales-support': {
    id: 'sales-support',
    name: 'Sales Support',
    slug: 'sales-support',
    icon: TrendingUp,
    servicePath: '/services/sales-support',
    capabilities: {
      technology: ['CRM management', 'Lead list building', 'Pipeline reporting', 'Proposal support', 'Meeting scheduling', 'Follow-up workflows'],
      staffing: ['Client development support', 'Business development coordination', 'Proposal preparation', 'Pipeline management', 'Meeting coordination', 'Sales reporting'],
      'financial-services': ['Client relationship support', 'Documentation preparation', 'Meeting coordination', 'Compliance documentation', 'Pipeline tracking', 'Reporting support']
    },
    benefits: ['Better CRM discipline', 'Increased sales efficiency', 'Improved pipeline visibility', 'More time for selling']
  }
};

// Generate all 12 initial solution pages data
const generateSolutionPageData = (serviceKey, industryKey) => {
  const service = solutionServices[serviceKey];
  const industry = solutionIndustries[industryKey];
  
  const slug = `${service.slug}-for-${industry.slug}`;
  const title = `${service.name} for ${industry.name}`;
  
  return {
    slug,
    title,
    service,
    industry,
    path: `/solutions/${slug}`,
    seoTitle: `${service.name} for ${industry.name} | APAC Center of Excellence`,
    seoDescription: `Outsourced ${service.name.toLowerCase()} support for ${industry.name.toLowerCase()}. Dedicated APAC-based teams to help you scale ${service.name.toLowerCase()} efficiently.`,
    capabilities: service.capabilities[industryKey] || service.capabilities.technology,
    benefits: service.benefits,
    challenges: industry.theme.challenges
  };
};

// All 12 initial solution pages
export const solutionPages = [
  // HR Operations for all industries
  generateSolutionPageData('hr-operations', 'technology'),
  generateSolutionPageData('hr-operations', 'staffing'),
  generateSolutionPageData('hr-operations', 'financial-services'),
  // Finance & Accounting for all industries
  generateSolutionPageData('finance-accounting', 'technology'),
  generateSolutionPageData('finance-accounting', 'staffing'),
  generateSolutionPageData('finance-accounting', 'financial-services'),
  // Business Operations for all industries
  generateSolutionPageData('business-operations', 'technology'),
  generateSolutionPageData('business-operations', 'staffing'),
  generateSolutionPageData('business-operations', 'financial-services'),
  // Recruitment Support for all industries
  generateSolutionPageData('recruitment-support', 'technology'),
  generateSolutionPageData('recruitment-support', 'staffing'),
  generateSolutionPageData('recruitment-support', 'financial-services')
];

// ============== LOCATION PAGES DATA ==============

// US Cities
export const usLocations = [
  { id: 'new-york', name: 'New York', state: 'NY', country: 'US' },
  { id: 'los-angeles', name: 'Los Angeles', state: 'CA', country: 'US' },
  { id: 'chicago', name: 'Chicago', state: 'IL', country: 'US' },
  { id: 'houston', name: 'Houston', state: 'TX', country: 'US' },
  { id: 'phoenix', name: 'Phoenix', state: 'AZ', country: 'US' },
  { id: 'philadelphia', name: 'Philadelphia', state: 'PA', country: 'US' },
  { id: 'san-antonio', name: 'San Antonio', state: 'TX', country: 'US' },
  { id: 'san-diego', name: 'San Diego', state: 'CA', country: 'US' },
  { id: 'dallas', name: 'Dallas', state: 'TX', country: 'US' },
  { id: 'san-jose', name: 'San Jose', state: 'CA', country: 'US' },
  { id: 'austin', name: 'Austin', state: 'TX', country: 'US' },
  { id: 'jacksonville', name: 'Jacksonville', state: 'FL', country: 'US' },
  { id: 'fort-worth', name: 'Fort Worth', state: 'TX', country: 'US' },
  { id: 'columbus', name: 'Columbus', state: 'OH', country: 'US' },
  { id: 'charlotte', name: 'Charlotte', state: 'NC', country: 'US' },
  { id: 'san-francisco', name: 'San Francisco', state: 'CA', country: 'US' },
  { id: 'indianapolis', name: 'Indianapolis', state: 'IN', country: 'US' },
  { id: 'seattle', name: 'Seattle', state: 'WA', country: 'US' },
  { id: 'denver', name: 'Denver', state: 'CO', country: 'US' },
  { id: 'washington-dc', name: 'Washington D.C.', state: 'DC', country: 'US' },
  { id: 'boston', name: 'Boston', state: 'MA', country: 'US' },
  { id: 'nashville', name: 'Nashville', state: 'TN', country: 'US' },
  { id: 'miami', name: 'Miami', state: 'FL', country: 'US' },
  { id: 'atlanta', name: 'Atlanta', state: 'GA', country: 'US' }
];

// Australia Cities
export const auLocations = [
  { id: 'sydney', name: 'Sydney', state: 'NSW', country: 'AU' },
  { id: 'melbourne', name: 'Melbourne', state: 'VIC', country: 'AU' },
  { id: 'brisbane', name: 'Brisbane', state: 'QLD', country: 'AU' },
  { id: 'perth', name: 'Perth', state: 'WA', country: 'AU' },
  { id: 'adelaide', name: 'Adelaide', state: 'SA', country: 'AU' },
  { id: 'gold-coast', name: 'Gold Coast', state: 'QLD', country: 'AU' },
  { id: 'canberra', name: 'Canberra', state: 'ACT', country: 'AU' },
  { id: 'newcastle', name: 'Newcastle', state: 'NSW', country: 'AU' },
  { id: 'wollongong', name: 'Wollongong', state: 'NSW', country: 'AU' },
  { id: 'geelong', name: 'Geelong', state: 'VIC', country: 'AU' }
];

export const allLocations = [...usLocations, ...auLocations];

// Featured services for location pages
const locationFeaturedServices = [
  { id: 'hr', title: 'HR Operations', path: '/services/hr', icon: Users },
  { id: 'finance', title: 'Finance & Accounting', path: '/services/finance', icon: DollarSign },
  { id: 'operations', title: 'Business Operations', path: '/services/operations', icon: Settings },
  { id: 'customer-support', title: 'Customer Support', path: '/services/customer-support', icon: Headphones },
  { id: 'recruitment', title: 'Recruitment Support', path: '/services/recruitment', icon: Search },
  { id: 'data-services', title: 'Data Services', path: '/services/data-services', icon: Database },
  { id: 'executive-assistants', title: 'Executive Assistants', path: '/services/executive-assistants', icon: CalendarCheck },
  { id: 'sales-support', title: 'Sales Support', path: '/services/sales-support', icon: TrendingUp }
];

// ============== SOLUTIONS INDEX PAGE ==============

export const SolutionsIndexPage = () => {
  const serviceGroups = {
    'HR Operations': solutionPages.filter(p => p.service.id === 'hr-operations'),
    'Finance & Accounting': solutionPages.filter(p => p.service.id === 'finance-accounting'),
    'Business Operations': solutionPages.filter(p => p.service.id === 'business-operations'),
    'Recruitment Support': solutionPages.filter(p => p.service.id === 'recruitment-support')
  };

  return (
    <div data-testid="solutions-index-page">
      <Helmet>
        <title>Industry-Specific Outsourcing Solutions | APAC Center of Excellence</title>
        <meta name="description" content="Explore our industry-specific outsourcing solutions for technology, staffing, and financial services companies. Dedicated teams tailored to your industry's needs." />
      </Helmet>

      {/* Hero */}
      <section className="section bg-[#0B1F3A] text-white">
        <div className="container mx-auto text-center">
          <p className="overline text-[#2F80ED] mb-4">Solutions</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Industry-Specific Outsourcing Solutions</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            We tailor our outsourcing services to the specific needs of your industry. Explore our solutions built for technology, staffing, and financial services companies.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact?type=book_call" className="btn-primary bg-[#2F80ED]" data-testid="solutions-hero-cta">
              Book a Call <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/services" className="btn-secondary bg-white/10 border-white/30 text-white hover:bg-white/20">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="section">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1F3A] mb-6">Outsourcing Built for Your Industry</h2>
            <p className="text-lg text-[#475569] leading-relaxed">
              Every industry has unique operational challenges. Our industry-specific solutions combine deep functional expertise with an understanding of your sector's specific requirements, helping you build more efficient and scalable operations.
            </p>
          </div>

          {/* Solutions by Service */}
          {Object.entries(serviceGroups).map(([serviceName, pages]) => (
            <div key={serviceName} className="mb-8">
              <h3 className="text-2xl font-bold text-[#0B1F3A] mb-6">{serviceName} Solutions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {pages.map(page => (
                  <Link 
                    key={page.slug} 
                    to={page.path} 
                    className="card p-6"
                    data-testid={`solution-card-${page.slug}`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <page.service.icon className="w-6 h-6 text-[#2F80ED]" />
                      <span className="text-sm font-medium text-[#2F80ED]">{page.industry.shortName}</span>
                    </div>
                    <h4 className="text-lg font-semibold text-[#0B1F3A] mb-2">{page.title}</h4>
                    <p className="text-sm text-[#475569] mb-4">
                      Outsourced {serviceName.toLowerCase()} tailored for {page.industry.name.toLowerCase()}.
                    </p>
                    <span className="text-[#2F80ED] font-medium inline-flex items-center gap-1 text-sm">
                      Learn more <ChevronRight className="w-4 h-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section section-surface">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0B1F3A] mb-6">Don't See Your Industry?</h2>
          <p className="text-lg text-[#475569] mb-8 max-w-2xl mx-auto">
            We work with companies across many industries. Contact us to discuss how we can build a solution tailored to your specific needs.
          </p>
          <Link to="/contact" className="btn-primary" data-testid="solutions-contact-cta">
            Contact Us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

// ============== SOLUTION PAGE COMPONENT ==============

export const SolutionPage = ({ slug }) => {
  const solution = solutionPages.find(s => s.slug === slug);
  
  if (!solution) {
    return (
      <div className="section text-center">
        <Helmet>
          <title>Solution Not Found | APAC Center of Excellence</title>
        </Helmet>
        <h1 className="text-2xl font-bold text-[#0B1F3A]">Solution not found</h1>
        <Link to="/solutions" className="text-[#2F80ED] mt-4 inline-block">View all solutions</Link>
      </div>
    );
  }

  const seoData = solutionContent[slug];
  const seoImages = getSolutionImages(solution.service.id, solution.industry.id);

  // Get related solutions (same service, different industry OR same industry, different service)
  const relatedSolutions = solutionPages
    .filter(s => s.slug !== slug && (s.service.id === solution.service.id || s.industry.id === solution.industry.id))
    .slice(0, 3);

  return (
    <div data-testid={`solution-page-${slug}`}>
      <Helmet>
        <title>{solution.seoTitle}</title>
        <meta name="description" content={solution.seoDescription} />
      </Helmet>

      {/* Hero */}
      <section className="section bg-[#0B1F3A] text-white">
        <div className="container mx-auto">
          <div className="max-w-4xl">
            <p className="overline text-[#2F80ED] mb-4">Solutions</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{solution.title}</h1>
            <p className="text-xl text-gray-300 mb-8">
              Build a more scalable {solution.service.name.toLowerCase()} function for your {solution.industry.shortName.toLowerCase()} business with dedicated outsourcing support from APAC Center of Excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?type=book_call" className="btn-primary bg-[#2F80ED]" data-testid="solution-hero-cta">
                Book a Call <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact?type=pricing" className="btn-secondary bg-white/10 border-white/30 text-white hover:bg-white/20">
                Request Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Introduction */}
      {seoData && (
        <section className="section">
          <div className="container mx-auto">
            <div className="max-w-4xl">
              <h2 className="text-3xl font-bold text-[#0B1F3A] mb-6">How We Support {solution.industry.name}</h2>
              <p className="text-lg text-[#475569] leading-relaxed">{seoData.intro}</p>
            </div>
            {seoImages.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 ">
                {seoImages.slice(0, 2).map((img, idx) => (
                  <img 
                    key={idx}
                    src={img.url} 
                    alt={img.alt}
                    loading="lazy"
                    className="w-full h-64 object-cover rounded-xl"
                    data-testid={`solution-seo-img-${slug}-${idx}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Fallback overview if no seoData */}
      {!seoData && (
        <section className="section">
          <div className="container mx-auto">
            <div className="max-w-4xl">
              <h2 className="text-3xl font-bold text-[#0B1F3A] mb-6">How We Support {solution.industry.name}</h2>
              <div className="prose prose-lg text-[#475569]">
                <p>
                  {solution.industry.name} face unique operational challenges that require specialized support. At APAC Center of Excellence, we provide dedicated {solution.service.name.toLowerCase()} teams that understand the specific requirements of your industry.
                </p>
                <p>
                  Our outsourcing model gives you access to skilled professionals who work as an extension of your team, aligned to your processes and systems.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Challenges */}
      <section className="section section-surface">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8">Common Challenges We Address</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solution.challenges.slice(0, 5).map((challenge, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <Target className="w-5 h-5 text-red-500" />
                </div>
                <p className="text-[#0B1F3A] font-medium">{challenge}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Extended SEO Content Sections */}
      {seoData && seoData.sections.map((section, idx) => (
        <React.Fragment key={idx}>
          <section className={`section ${idx % 2 === 0 ? '' : 'section-surface'}`}>
            <div className="container mx-auto">
              <div className="max-w-4xl">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3A] mb-6">{section.heading}</h2>
                {section.content.split('\n\n').map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-[#475569] leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
              {seoImages.length > 2 && idx === 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5 ">
                  {seoImages.slice(2, 4).map((img, imgIdx) => (
                    <img 
                      key={imgIdx}
                      src={img.url} 
                      alt={img.alt}
                      loading="lazy"
                      className="w-full h-56 object-cover rounded-xl"
                      data-testid={`solution-seo-img-${slug}-${imgIdx + 2}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        </React.Fragment>
      ))}

      {/* Capabilities */}
      <section className="section section-surface">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8">How We Help</h2>
              <p className="text-[#475569] mb-8">
                Our {solution.service.name.toLowerCase()} support for {solution.industry.name.toLowerCase()} includes:
              </p>
              <div className="space-y-4">
                {solution.capabilities.map((capability, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#2F80ED]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-[#2F80ED]" />
                    </div>
                    <span className="text-[#475569]">{capability}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8">Benefits</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {solution.benefits.map((benefit, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-xl">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="font-medium text-[#0B1F3A]">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Delivery Model */}
      <section className="section">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-[#0B1F3A] mb-6">Our Delivery Model</h2>
            <p className="text-lg text-[#475569] mb-6">
              We provide dedicated APAC-based professionals who become an extension of your team.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { icon: Users, title: 'Dedicated Team', desc: 'Professionals working exclusively for you' },
                { icon: Settings, title: 'Your Processes', desc: 'Aligned to your workflows and systems' },
                { icon: Globe, title: 'Integrated', desc: 'Seamless extension of your operations' },
                { icon: Zap, title: 'Scalable', desc: 'Grow your team as demand increases' }
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-14 h-14 bg-[#2F80ED]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-[#2F80ED]" />
                  </div>
                  <h4 className="font-semibold text-[#0B1F3A] mb-2">{item.title}</h4>
                  <p className="text-sm text-[#475569]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Solutions & Services */}
      <section className="section section-surface">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8 text-center">Related Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6  mx-auto mb-6">
            {relatedSolutions.map(related => (
              <Link key={related.slug} to={related.path} className="card p-6" data-testid={`related-solution-${related.slug}`}>
                <related.service.icon className="w-6 h-6 text-[#2F80ED] mb-3" />
                <h4 className="font-semibold text-[#0B1F3A] mb-2">{related.title}</h4>
                <span className="text-[#2F80ED] text-sm font-medium inline-flex items-center gap-1">
                  Learn more <ChevronRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
          <div className="text-center space-x-4">
            <Link to={solution.service.servicePath} className="text-[#2F80ED] font-medium hover:underline">
              View {solution.service.name} Service →
            </Link>
            <Link to={`/industries/${solution.industry.id}`} className="text-[#2F80ED] font-medium hover:underline">
              View {solution.industry.shortName} Industry →
            </Link>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <InternalLinks currentPage={`solution-${slug}`} excludeServices={[solution.service.id]} />

      {/* Final CTA */}
      <section className="section bg-[#0B1F3A]">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Build a More Scalable {solution.service.name} Function
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Ready to transform your {solution.service.name.toLowerCase()} operations? Let's discuss how we can help.
          </p>
          <Link to="/contact?type=book_call" className="btn-primary bg-[#2F80ED]" data-testid="solution-final-cta">
            Book a Call <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

// ============== LOCATIONS INDEX PAGE ==============

export const LocationsIndexPage = () => {
  return (
    <div data-testid="locations-index-page">
      <Helmet>
        <title>Locations | Outsourcing Services for US & Australia | APAC Center of Excellence</title>
        <meta name="description" content="APAC Center of Excellence supports businesses across the United States and Australia with dedicated outsourcing teams. Find support for your city." />
      </Helmet>

      {/* Hero */}
      <section className="section bg-[#0B1F3A] text-white">
        <div className="container mx-auto text-center">
          <p className="overline text-[#2F80ED] mb-4">Locations</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Outsourcing Services for Growing Companies</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            APAC Center of Excellence supports businesses across the United States and Australia with dedicated outsourcing teams. Wherever you're located, we can help you scale.
          </p>
          <Link to="/contact?type=book_call" className="btn-primary bg-[#2F80ED]" data-testid="locations-hero-cta">
            Book a Call <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* How We Support */}
      <section className="section">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1F3A] mb-6">How We Support Your Business</h2>
            <p className="text-lg text-[#475569] leading-relaxed">
              We provide dedicated APAC-based teams that work remotely with your business, integrated into your workflows and systems. Our model allows companies in any location to access skilled professionals without the overhead of traditional hiring.
            </p>
          </div>
        </div>
      </section>

      {/* US Cities */}
      <section className="section section-surface">
        <div className="container mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-[#0B1F3A] rounded-xl flex items-center justify-center">
              <span className="text-white text-xl">🇺🇸</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3A]">United States</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {usLocations.map(location => (
              <Link 
                key={location.id} 
                to={`/locations/${location.id}`} 
                className="bg-white p-4 rounded-lg border border-gray-200 hover:border-[#2F80ED] hover:shadow-md transition-all text-center"
                data-testid={`location-link-${location.id}`}
              >
                <MapPin className="w-5 h-5 text-[#2F80ED] mx-auto mb-2" />
                <span className="font-medium text-[#0B1F3A] text-sm">{location.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Australia Cities */}
      <section className="section">
        <div className="container mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-[#0B1F3A] rounded-xl flex items-center justify-center">
              <span className="text-white text-xl">🇦🇺</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3A]">Australia</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {auLocations.map(location => (
              <Link 
                key={location.id} 
                to={`/locations/${location.id}`} 
                className="bg-[#F7F9FC] p-4 rounded-lg border border-gray-200 hover:border-[#2F80ED] hover:shadow-md transition-all text-center"
                data-testid={`location-link-${location.id}`}
              >
                <MapPin className="w-5 h-5 text-[#2F80ED] mx-auto mb-2" />
                <span className="font-medium text-[#0B1F3A] text-sm">{location.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-surface">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0B1F3A] mb-6">Ready to Build Your Team?</h2>
          <p className="text-lg text-[#475569] mb-8 max-w-2xl mx-auto">
            No matter where your business is located, we can help you build a scalable outsourcing team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact?type=book_call" className="btn-primary">
              Book a Call <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/services" className="btn-secondary">
              Explore Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============== LOCATION PAGE COMPONENT ==============

export const LocationPage = ({ locationId }) => {
  const location = allLocations.find(l => l.id === locationId);
  
  if (!location) {
    return (
      <div className="section text-center">
        <Helmet>
          <title>Location Not Found | APAC Center of Excellence</title>
        </Helmet>
        <h1 className="text-2xl font-bold text-[#0B1F3A]">Location not found</h1>
        <Link to="/locations" className="text-[#2F80ED] mt-4 inline-block">View all locations</Link>
      </div>
    );
  }

  const isUS = location.country === 'US';
  const countryName = isUS ? 'United States' : 'Australia';
  const fullLocation = location.state ? `${location.name}, ${location.state}` : location.name;
  const pageTitle = `Outsourcing Services in ${location.name} | APAC Center of Excellence`;
  const pageDescription = `APAC Center of Excellence helps businesses in ${location.name} scale HR, finance, operations, and support functions with dedicated outsourcing teams.`;

  const seoData = getLocationContent(locationId, location.name, location.state, location.country);
  const seoImages = getLocationImages(location.name, location.country);

  // Get some related solutions to link
  const relatedSolutions = solutionPages.slice(0, 3);

  return (
    <div data-testid={`location-page-${locationId}`}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>

      {/* Hero */}
      <section className="section bg-[#0B1F3A] text-white">
        <div className="container mx-auto">
          <div>
            <p className="overline text-[#2F80ED] mb-4">Locations</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Outsourcing Services for Companies in {location.name}</h1>
            <p className="text-xl text-gray-300 mb-8">
              APAC Center of Excellence helps businesses in {fullLocation} scale HR, finance, operations, and support functions with dedicated outsourcing teams.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?type=book_call" className="btn-primary bg-[#2F80ED]" data-testid="location-hero-cta">
                Book a Call <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/services" className="btn-secondary bg-white/10 border-white/30 text-white hover:bg-white/20">
                Explore Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Introduction */}
      {seoData && (
        <section className="section">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-[#0B1F3A] mb-6">Supporting {location.name} Businesses</h2>
            <p className="text-lg text-[#475569] leading-relaxed">{seoData.intro}</p>
            {seoImages.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                {seoImages.slice(0, 2).map((img, idx) => (
                  <img 
                    key={idx}
                    src={img.url} 
                    alt={img.alt}
                    loading="lazy"
                    className="w-full h-64 object-cover rounded-xl"
                    data-testid={`location-seo-img-${locationId}-${idx}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Fallback if no seoData */}
      {!seoData && (
        <section className="section">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-[#0B1F3A] mb-6">Supporting {location.name} Businesses</h2>
            <div className="prose prose-lg text-[#475569]">
              <p>
                Growing businesses in {location.name} face increasing pressure to scale operations while managing costs. Whether you're a fast-growing startup or an established enterprise, operational efficiency is critical to staying competitive.
              </p>
              <p>
                APAC Center of Excellence provides dedicated outsourcing teams that work remotely with companies in {fullLocation}, delivering HR, finance, operations, and administrative support without the overhead of traditional hiring.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Services */}
      <section className="section section-surface">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8">Services for {location.name} Companies</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {locationFeaturedServices.map(service => (
              <Link key={service.id} to={service.path} className="card p-6" data-testid={`location-service-${service.id}`}>
                <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-lg flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-[#2F80ED]" />
                </div>
                <h3 className="font-semibold text-[#0B1F3A] mb-2">{service.title}</h3>
                <span className="text-[#2F80ED] text-sm font-medium inline-flex items-center gap-1">
                  Learn more <ChevronRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Extended SEO Content Sections */}
      {seoData && seoData.sections.map((section, idx) => (
        <React.Fragment key={idx}>
          <section className={`section ${idx % 2 === 0 ? '' : 'section-surface'}`}>
            <div className="container mx-auto">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3A] mb-6">{section.heading}</h2>
                {section.content.split('\n\n').map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-[#475569] leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
              {/* Distribute remaining images across content sections */}
              {idx === 0 && seoImages.length > 2 && (
                <div className="mt-6">
                  <img 
                    src={seoImages[2].url} 
                    alt={seoImages[2].alt}
                    loading="lazy"
                    className="w-full h-72 object-cover rounded-xl"
                    data-testid={`location-seo-img-${locationId}-2`}
                  />
                </div>
              )}
              {idx === 2 && seoImages.length > 3 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  {seoImages.slice(3, 5).map((img, imgIdx) => (
                    <img 
                      key={imgIdx}
                      src={img.url} 
                      alt={img.alt}
                      loading="lazy"
                      className="w-full h-56 object-cover rounded-xl"
                      data-testid={`location-seo-img-${locationId}-${imgIdx + 3}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        </React.Fragment>
      ))}

      {/* Why Work With Us */}
      <section className="section section-surface">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8 text-center">Why {location.name} Companies Work With Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: TrendingUp, title: 'Lower Operating Costs', desc: 'Reduce overhead by up to 60% compared to local hiring' },
              { icon: Users, title: 'Scalable Capacity', desc: 'Grow your team quickly as your business grows' },
              { icon: Shield, title: 'Dedicated Support', desc: 'Teams that work exclusively for your business' },
              { icon: Zap, title: 'Operational Reliability', desc: 'Consistent, high-quality work aligned to your standards' }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-14 h-14 bg-[#2F80ED]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-[#2F80ED]" />
                </div>
                <h4 className="font-semibold text-[#0B1F3A] mb-2">{item.title}</h4>
                <p className="text-sm text-[#475569]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8">Industries We Support in {location.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mx-auto">
            {[
              { name: 'Technology', path: '/industries/technology', desc: 'Support for fast-growing tech companies' },
              { name: 'Staffing', path: '/industries/staffing', desc: 'Administrative support for staffing firms' },
              { name: 'Financial Services', path: '/industries/financial-services', desc: 'Operations support for financial firms' }
            ].map(industry => (
              <Link key={industry.name} to={industry.path} className="card p-6 text-center">
                <Building2 className="w-8 h-8 text-[#2F80ED] mx-auto mb-4" />
                <h4 className="font-semibold text-[#0B1F3A] mb-2">{industry.name}</h4>
                <p className="text-sm text-[#475569] mb-3">{industry.desc}</p>
                <span className="text-[#2F80ED] text-sm font-medium">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section section-surface">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8 text-center">How the Model Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mx-auto">
            {[
              { step: 1, title: 'Define Needs', desc: 'We assess your requirements' },
              { step: 2, title: 'Build Team', desc: 'We source and vet candidates' },
              { step: 3, title: 'Integrate', desc: 'We align with your workflows' },
              { step: 4, title: 'Scale', desc: 'Grow support as needed' }
            ].map(item => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 bg-[#2F80ED] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                  {item.step}
                </div>
                <h4 className="font-semibold text-[#0B1F3A] mb-2">{item.title}</h4>
                <p className="text-sm text-[#475569]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="section">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8 text-center">Explore More</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/services" className="btn-secondary">All Services</Link>
            <Link to="/solutions" className="btn-secondary">Industry Solutions</Link>
            <Link to="/how-it-works" className="btn-secondary">How It Works</Link>
            <Link to="/contact" className="btn-secondary">Contact Us</Link>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <InternalLinks currentPage={`location-${locationId}`} excludeLocations={[locationId]} />

      {/* Final CTA */}
      <section className="section bg-[#0B1F3A]">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Build a More Scalable Team in {location.name}
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Ready to explore outsourcing support for your {location.name} business? Let's discuss your needs.
          </p>
          <Link to="/contact?type=book_call" className="btn-primary bg-[#2F80ED]" data-testid="location-final-cta">
            Book a Call <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Export helper function to get all solution slugs
export const getSolutionSlugs = () => solutionPages.map(s => s.slug);

// Export helper function to get all location slugs
export const getLocationSlugs = () => allLocations.map(l => l.id);
